import { gql, GraphQLClient } from "graphql-request";
import { createGraphqlClient, type GraphqlRequestConfig } from "./client";

// ========== GraphQL Queries ==========

const GET_MY_LEARNING_STATS_QUERY = gql`
  query GetMyLearningStats {
    myLearningStats {
      weeklyStats {
        date
        count
      }
      dailyStreak
      weeklyStreak
      totalCompletedLessons
      daysSinceFirstLesson
      todayCompletedLessons
    }
  }
`;

const GET_LESSON_LEARNING_PROGRESS_QUERY = gql`
  query GetLessonLearningProgress {
    myLearningStats {
      weeklyStats { date count }
      dailyStreak
      weeklyStreak
      totalCompletedLessons
      daysSinceFirstLesson
      todayCompletedLessons
    }
    getUserSubmittedLessons
  }
`;

// ========== Types ==========

export interface WeeklyStatItem {
  date: string;
  count: number;
}

export interface LearningStats {
  weeklyStats: WeeklyStatItem[];
  dailyStreak: number;
  weeklyStreak: number;
  totalCompletedLessons: number;
  daysSinceFirstLesson: number;
  todayCompletedLessons: number;
}

export interface GraphqlLearningStatsOptions extends GraphqlRequestConfig {
  client?: GraphQLClient;
}

function ensureClient(options?: GraphqlLearningStatsOptions) {
  if (options?.client) {
    return options.client;
  }
  return createGraphqlClient(options);
}

// ========== API Functions ==========

/**
 * 現在のユーザーの学習統計情報を取得
 */
export async function getMyLearningStats(
  options?: GraphqlLearningStatsOptions
): Promise<LearningStats | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_LEARNING_STATS_QUERY);
  return (data as any).myLearningStats;
}

export type LessonLearningProgress = Readonly<{
  learningStats: LearningStats | null;
  submittedLessonIds: string[] | null;
}>;

/** Fetch the two personalized lesson-loader projections in one operation. */
export async function getLessonLearningProgress(
  options?: GraphqlLearningStatsOptions,
): Promise<LessonLearningProgress> {
  const client = ensureClient(options);
  const data = await client.request(GET_LESSON_LEARNING_PROGRESS_QUERY);
  return {
    learningStats: (data as any).myLearningStats ?? null,
    submittedLessonIds: (data as any).getUserSubmittedLessons ?? null,
  };
}

const GET_MY_YEARLY_ACTIVITY_QUERY = gql`
  query GetMyYearlyActivity($year: Int) {
    myYearlyActivity(year: $year) {
      date
      count
    }
  }
`;

export interface DailyActivity {
  /** YYYY-MM-DD */
  date: string;
  count: number;
}

/**
 * 年間の学習日を取得する (GitHub 風の草)。
 *
 * 活動が無かった日は返ってこない。365 日分の枠は画面側で作って、
 * ここで返った日だけ埋める
 */
export async function getMyYearlyActivity(
  year?: number,
  options?: GraphqlLearningStatsOptions
): Promise<DailyActivity[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_YEARLY_ACTIVITY_QUERY, { year });
  return ((data as any).myYearlyActivity ?? []) as DailyActivity[];
}

const GET_LEARNER_RANKING_QUERY = gql`
  query GetLearnerRanking($category: String!, $limit: Int) {
    learnerRanking(category: $category, limit: $limit) {
      displayName
      rank
      value
      unit
    }
  }
`;

export interface RankingRow {
  displayName: string;
  rank: number;
  value: number;
  unit: string;
}

/** 殿堂の順位。category は xp | lessons */
export async function getLearnerRanking(
  category: string,
  limit?: number,
  options?: GraphqlLearningStatsOptions
): Promise<RankingRow[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_LEARNER_RANKING_QUERY, { category, limit });
  return ((data as any).learnerRanking ?? []) as RankingRow[];
}

const GET_POPULAR_COURSE_SLUGS_QUERY = gql`
  query GetPopularCourseSlugs($limit: Int) {
    popularCourseSlugs(limit: $limit)
  }
`;

/** 実際に取り組まれている順のコース slug。人数は返らない */
export async function getPopularCourseSlugs(
  limit?: number,
  options?: GraphqlLearningStatsOptions
): Promise<string[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_POPULAR_COURSE_SLUGS_QUERY, { limit });
  return ((data as any).popularCourseSlugs ?? []) as string[];
}
